import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoldButton } from '@/components/common/GoldButton';
import { useAuth } from '@/context/AuthContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { t } from '@/i18n';
import {
  canonicalizeEmailForSubmit,
  getEmailValidationError,
  getNewPasswordError,
  isSupportedEmail,
  MAX_EMAIL_LENGTH,
  newPasswordsMatch,
} from '@/utils/authValidation';
import styles from './LoginForm.module.css';

export function RegisterForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState({
    email: false,
    password: false,
    confirm: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();
  const onApiError = useApiErrorToast();

  const emailErr = touched.email && !isSupportedEmail(email);
  const passwordError = getNewPasswordError(password);
  const passErr = touched.password && passwordError !== null;
  const matchErr = touched.confirm && !newPasswordsMatch(password, confirm);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true, confirm: true });
    const submittedEmail = canonicalizeEmailForSubmit(email);
    if (
      !isSupportedEmail(email) ||
      getNewPasswordError(password) !== null ||
      !newPasswordsMatch(password, confirm) ||
      submitting
    )
      return;
    setSubmitting(true);
    try {
      await register({ email: submittedEmail, password });
      navigate(
        `/verify-email?email=${encodeURIComponent(submittedEmail)}&sent=1`,
        { replace: true },
      );
    } catch (err) {
      onApiError(err, t.auth.registerFailed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <div className={styles.grp}>
        <div className={styles.lbl}>{t.common.emailLabel}</div>
        <input
          className={[styles.inp, emailErr ? styles.err : '']
            .filter(Boolean)
            .join(' ')}
          autoComplete="email"
          autoCapitalize="none"
          inputMode="email"
          spellCheck={false}
          type="email"
          required
          maxLength={MAX_EMAIL_LENGTH}
          placeholder={t.common.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched((value) => ({ ...value, email: true }))}
          disabled={submitting}
        />
        <div
          className={[styles.e, emailErr ? styles.eShow : '']
            .filter(Boolean)
            .join(' ')}
        >
          {getEmailValidationError(email) === 'internationalLocalPart'
            ? t.common.emailInternationalUnsupported
            : t.common.emailInvalid}
        </div>
      </div>
      <div className={styles.grp}>
        <div className={styles.lbl}>{t.common.passwordLabel}</div>
        <input
          className={[styles.inp, passErr ? styles.err : '']
            .filter(Boolean)
            .join(' ')}
          autoComplete="new-password"
          type="password"
          placeholder={t.common.passwordMin8Placeholder}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => setTouched((value) => ({ ...value, password: true }))}
          disabled={submitting}
        />
        <div
          className={[styles.e, passErr ? styles.eShow : '']
            .filter(Boolean)
            .join(' ')}
        >
          {passwordError === 'tooLong'
            ? t.common.passwordMax128
            : t.common.passwordMin8}
        </div>
      </div>
      <div className={styles.grp}>
        <div className={styles.lbl}>{t.auth.confirmPasswordLabel}</div>
        <input
          className={[styles.inp, matchErr ? styles.err : '']
            .filter(Boolean)
            .join(' ')}
          autoComplete="new-password"
          type="password"
          required
          placeholder={t.common.passwordPlaceholder}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onBlur={() => setTouched((value) => ({ ...value, confirm: true }))}
          disabled={submitting}
        />
        <div
          className={[styles.e, matchErr ? styles.eShow : '']
            .filter(Boolean)
            .join(' ')}
        >
          {t.common.passwordsMustMatch}
        </div>
      </div>
      <GoldButton type="submit" disabled={submitting}>
        {submitting ? t.auth.registerSubmitting : t.auth.register}
      </GoldButton>
    </form>
  );
}
